/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from 'react';
import { useTranslation } from 'react-i18next';

import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import ActivitiesCompletedTimeSpentChart from '../../../charts/engagement/ActivitiesCompletedTimeSpentChart';
import OverallCurriculumProgressChart from '../../../charts/engagement/OverallCurriculumProgressChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { KeyClassroomEngagementStudentResult } from 'types/schema-generated-types';
import { ReportEntityType } from 'components/ReportsModule/types';

const EngagementOverviewChartCards: React.FC<{
  reportData: KeyClassroomEngagementStudentResult[];
  loading: boolean;
}> = ({ reportData = [], loading }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCharts', 'ReportsHelpCommon']);
  const [, setQuery] = useReportQueryParams();

  const {
    ids,
    studentNames,
    curriculumProgressSeries,
    activitiesCompletedSeries,
    timeSpentSeries
  } = reportData.reduce(
    (
      acc,
      {
        student,
        curriculumProgressPercentage,
        totalActivitiesCompletedCount,
        averageWeeklyMinutesSpent
      }
    ) => {
      if (!student) return acc;
      const { studentNames, ...stats } = acc;

      studentNames.push(`${student.firstName} ${student.lastName}`);
      stats.ids.push(student.id);
      stats.curriculumProgressSeries.push(curriculumProgressPercentage ?? 0);
      stats.activitiesCompletedSeries.push(totalActivitiesCompletedCount ?? 0);
      stats.timeSpentSeries.push(averageWeeklyMinutesSpent ?? 0);
      return acc;
    },
    {
      ids: [] as number[],
      studentNames: [] as string[],
      curriculumProgressSeries: [] as number[],
      activitiesCompletedSeries: [] as number[],
      timeSpentSeries: [] as number[]
    }
  );

  const handleChartClick = (index: number) => {
    setQuery({ entityType: ReportEntityType.STUDENT, entityId: ids[index] }, 'pushIn');
  };

  return (
    <>
      <ReportCard
        title={t('Overall Curriculum Progress')}
        helpText={t('curriculumProgressByStudent', { ns: 'ReportsHelpCommon' })}
        loading={loading}
        reportData={reportData}
      >
        <OverallCurriculumProgressChart
          series={curriculumProgressSeries}
          labels={studentNames}
          onClick={handleChartClick}
        />
      </ReportCard>
      <ReportCard
        title={t('Activities & Average Weekly Time Spent')}
        helpText={t('activitiesCompletedAndTimeSpentByStudent', {
          ns: 'ReportsHelpCharts'
        })}
        loading={loading}
        reportData={reportData}
      >
        <ActivitiesCompletedTimeSpentChart
          activitiesCompletedSeries={activitiesCompletedSeries}
          timeSpentSeries={timeSpentSeries}
          labels={studentNames}
          onClick={handleChartClick}
        />
      </ReportCard>
    </>
  );
};

export default EngagementOverviewChartCards;
